import React, { useState, useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import MusicPlayer, { detectMood } from '../components/MusicPlayer';
// ElevenLabs SDK removed - using widget approach

marked.setOptions({ gfm: true, breaks: true });

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  type?: 'text' | 'image' | 'card' | 'voice' | 'action';
  metadata?: Record<string, any>;
  created_at?: string;
  timestamp?: number;
  error?: boolean;
}

const fmtTime = (ts?: number | string) => {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : new Date(ts);
  return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (ts?: number | string) => {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (today.getTime() - msgDay.getTime()) / 86400000;
  if (diff === 0) return '今天';
  if (diff === 1) return '昨天';
  return d.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
};

const stripTags = (t: string) => t.replace(/\[(?:IMAGE|EDIT_IMAGE|FILE|REMINDER|SEND_RECENT_IMAGE|ENABLE_NOTIFICATIONS|ADD_TO_HOMESCREEN|VOICE|SET_NAME|SET_AVATAR|OPTION|ACTION|CARD|TYPING|SYSTEM|CALL|HANGUP|TTS|SOUND|NOTIFICATION|PROACTIVE|SCHEDULE|CONTEXT|MOOD|SETTING)(?::[^\]]*)?\]/gi, '').replace(/^VOICE[:：].+$/gim, '').trim();
const parseOptions = (content: string): { text: string; options: string[] } => {
  const options: string[] = [];
  const text = content.replace(/\[OPTION:([^\]]+)\]/g, (_, opt) => { options.push(opt); return ''; });
  return { text, options };
};
const md = (text: string) => {
  const html = DOMPurify.sanitize(marked.parse(stripTags(text)) as string, {ADD_ATTR: ['target']});
  return html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
};

// Split assistant messages into separate bubbles — only for short conversational text
const splitMessages = (msgs: ChatMessage[]): ChatMessage[] => {
  const result: ChatMessage[] = [];
  for (const msg of msgs) {
    if (msg.role === 'assistant' && msg.content && msg.content.includes('\n\n')) {
      const parts = msg.content.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
      // Only split if: <= 3 parts, all parts are short, no structured content (lists/numbers)
      const isStructured = parts.some(p => /^[\d•\-\*]|\n[\d•\-\*]/.test(p) || p.length > 40);
      if (parts.length > 1 && parts.length <= 3 && !isStructured) {
        parts.forEach((part, i) => {
          result.push({ ...msg, content: part, timestamp: (msg.timestamp || 0) + i, created_at: msg.created_at });
        });
        continue;
      }
    }
    result.push(msg);
  }
  return result;
};

const sameGroup = (a: ChatMessage, b: ChatMessage) => {
  if (a.role !== b.role) return false;
  const ta = a.created_at ? new Date(a.created_at).getTime() : (a.timestamp || 0);
  const tb = b.created_at ? new Date(b.created_at).getTime() : (b.timestamp || 0);
  return Math.abs(ta - tb) < 300000;
};

const diffDay = (a: ChatMessage, b: ChatMessage) => {
  const da = a.created_at ? new Date(a.created_at) : new Date(a.timestamp || 0);
  const db = b.created_at ? new Date(b.created_at) : new Date(b.timestamp || 0);
  return da.toDateString() !== db.toDateString();
};

const Heart = ({ s = 16 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function ChatApp() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [waitingFirstMsg, setWaitingFirstMsg] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [coldStartHint, setColdStartHint] = useState('');
  const [lightbox, setLightbox] = useState<string|null>(null);
  const [ttsPlaying, setTtsPlaying] = useState<number|null>(null);
  const [ttsDurations, setTtsDurations] = useState<Record<number,number>>({});
  const ttsAudioRef = useRef<HTMLAudioElement|null>(null);

  const playTTS = async (text: string, idx: number, audioUrl?: string) => {
    // Stop if already playing this message
    if (ttsPlaying === idx && ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
      setTtsPlaying(null);
      return;
    }
    // Stop any existing playback
    if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    setTtsPlaying(idx);
    try {
      let url: string;
      let needRevoke = false;
      if (audioUrl) {
        // Fetch audio file with credentials, create blob URL for replay
        const audioResp = await fetch(audioUrl, { credentials: 'include' });
        if (!audioResp.ok) throw new Error('Audio fetch failed');
        const audioBlob = await audioResp.blob();
        url = URL.createObjectURL(audioBlob);
        needRevoke = true;
      } else {
        // Generate TTS on the fly
        const cleanText = text.replace(/\[(?:IMAGE|EDIT_IMAGE|FILE|REMINDER|SEND_RECENT_IMAGE|ENABLE_NOTIFICATIONS|ADD_TO_HOMESCREEN|VOICE):[^\]]*\]/gi, '').replace(/\[(?:SEND_RECENT_IMAGE|ENABLE_NOTIFICATIONS|ADD_TO_HOMESCREEN|VOICE)\]/gi, '').trim();
        if (!cleanText) { setTtsPlaying(null); return; }
        const resp = await fetch('/api/chat/tts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: cleanText.slice(0, 2000) }),
        });
        if (!resp.ok) throw new Error('TTS failed');
        const blob = await resp.blob();
        url = URL.createObjectURL(blob);
        needRevoke = true;
      }
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      audio.onloadedmetadata = () => {
        if (audio.duration && isFinite(audio.duration)) {
          setTtsDurations(prev => ({...prev, [idx]: Math.round(audio.duration)}));
        }
      };
      audio.onended = () => { setTtsPlaying(null); ttsAudioRef.current = null; if (needRevoke) URL.revokeObjectURL(url); };
      audio.onerror = () => { setTtsPlaying(null); ttsAudioRef.current = null; if (needRevoke) URL.revokeObjectURL(url); };
      await audio.play();
    } catch (e) {
      console.error('[tts]', e);
      setTtsPlaying(null);
    }
  };
  const [stream, setStream] = useState('');
  const [input, setInput] = useState('');
  const [loadedAt] = useState(() => Date.now());
  const [inCall, setInCall] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [showInstallHint, setShowInstallHint] = useState(false);
  const [showNotifHint, setShowNotifHint] = useState(false);
  const [showPwaGuide, setShowPwaGuide] = useState(false);
  const [filePreview, setFilePreview] = useState<{url:string,name:string}|null>(null);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [aiName, setAiName] = useState(() => { try { return localStorage.getItem('ai_name') || 'AI超级男友'; } catch { return 'AI超级男友'; } });
  const [aiAvatar, setAiAvatar] = useState<string|null>(() => { try { return localStorage.getItem('ai_avatar'); } catch { return null; } });
  const [credits, setCredits] = useState<number|null>(null);
  const [enhancedUI, setEnhancedUI] = useState(false);
  const [currentHour] = useState(() => new Date().getHours());
  const [containerStatus, setContainerStatus] = useState<'online'|'offline'|'loading'>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const mediaRecRef = useRef<MediaRecorder|null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recTimerRef = useRef<ReturnType<typeof setInterval>|null>(null);
  const deferredPromptRef = useRef<any>(null);
  const callTranscript = useRef<{role:string,content:string}[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const scroll = useCallback(() => {
    requestAnimationFrame(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; });
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !messages.length) return;
    setLoadingMore(true);
    try {
      const oldest = messages[0]?.created_at || messages[0]?.timestamp;
      if (!oldest) return;
      const r = await fetch(`/api/chat/history?before=${encodeURIComponent(oldest)}&limit=50`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        const older = splitMessages(d.messages || []);
        if (older.length < 50) setHasMore(false);
        if (older.length > 0) {
          const prevH = listRef.current?.scrollHeight || 0;
          setMessages(prev => [...older, ...prev]);
          // Maintain scroll position after prepending
          requestAnimationFrame(() => {
            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight - prevH;
          });
        }
      }
    } catch {} finally { setLoadingMore(false); }
  }, [loadingMore, hasMore, messages]);

  const wsRef = useRef<WebSocket|null>(null);
  const micStreamRef = useRef<MediaStream|null>(null);
  const micCtxRef = useRef<AudioContext|null>(null);
  const playCtxRef = useRef<AudioContext|null>(null);
  const micProcRef = useRef<ScriptProcessorNode|null>(null);
  const callTimerRef = useRef<ReturnType<typeof setInterval>|null>(null);
  const [callSeconds, setCallSeconds] = useState(0);
  const callSecondsRef = useRef(0);
  const [callMuted, setCallMuted] = useState(false);
  const [incomingCall, setIncomingCall] = useState(false);
  const [incomingCallOpener, setIncomingCallOpener] = useState('');
  const incomingCallAudioRef = useRef<HTMLAudioElement|null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);

  function float32ToInt16(f32: Float32Array) {
    const i16 = new Int16Array(f32.length);
    for (let i = 0; i < f32.length; i++) {
      const s = Math.max(-1, Math.min(1, f32[i]));
      i16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return i16;
  }
  function bufToBase64(buf: ArrayBuffer) {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  }
  function base64ToBuf(b64: string) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  }

  const nextPlayTimeRef = useRef(0);

  function playAudioChunk(b64: string) {
    const ctx = playCtxRef.current;
    if (!ctx) return;
    // Mute mic while AI is speaking
    const micTrack = micStreamRef.current?.getAudioTracks()[0];
    if (micTrack) micTrack.enabled = false;

    const pcm = base64ToBuf(b64);
    const i16 = new Int16Array(pcm);
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
    const audioBuf = ctx.createBuffer(1, f32.length, 16000);
    audioBuf.getChannelData(0).set(f32);

    const src = ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(ctx.destination);

    // Schedule seamlessly: each chunk starts right after the previous ends
    const now = ctx.currentTime;
    const startAt = Math.max(now, nextPlayTimeRef.current);
    src.start(startAt);
    nextPlayTimeRef.current = startAt + audioBuf.duration;

    // Unmute mic after this chunk ends (will be overridden by next chunk if more come)
    src.onended = () => {
      if (ctx.currentTime >= nextPlayTimeRef.current - 0.05) {
        if (micTrack) micTrack.enabled = true;
      }
    };
  }

  const startCall = async () => {
    if (inCall) return;
    setInCall(true);
    setCallStatus('connecting');
    setCallSeconds(0);
    try {
      const resp = await fetch('/api/chat/voice/start', { method: 'POST', credentials: 'include' });
      if (!resp.ok) throw new Error('获取通话配置失败');
      const data = await resp.json();
      if (!data.signed_url) throw new Error('未获取到通话地址');

      // Request mic (will be remembered after first grant)
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });

      playCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      await playCtxRef.current.resume();

      const ws = new WebSocket(data.signed_url);
      wsRef.current = ws;

      ws.onopen = () => {
        setCallStatus('waiting');
        ws.send(JSON.stringify({
          type: 'conversation_initiation_client_data',
          conversation_initiation_client_data: {
            conversation_config_override: {
              agent: {
                prompt: { prompt: data.context_prompt || '' },
                first_message: '喂？宝贝~',
                language: 'zh'
              },
              tts: {
                voice_id: 'WDaMihIzkinmAFqR4YCS',
                stability: 0.3,
                similarity_boost: 0.85,
                style: 0.7,
                use_speaker_boost: true
              }
            }
          }
        }));
      };

  const answerIncomingCall = () => {
    setIncomingCall(false);
    if (incomingCallAudioRef.current) { incomingCallAudioRef.current.pause(); incomingCallAudioRef.current = null; }
    startCall();
  };

  const rejectIncomingCall = () => {
    setIncomingCall(false);
    if (incomingCallAudioRef.current) { incomingCallAudioRef.current.pause(); incomingCallAudioRef.current = null; }
  };

      ws.onmessage = (event) => {
        let msg: any;
        try { msg = JSON.parse(event.data); } catch { return; }
        switch (msg.type) {
          case 'conversation_initiation_metadata':
            setCallStatus('connected');
            // Start timer
            callTimerRef.current = setInterval(() => setCallSeconds(s => { callSecondsRef.current = s + 1; return s + 1; }), 1000);
            // Start mic
            startMicCapture();
            break;
          case 'audio':
            if (msg.audio_event?.audio_base_64) {
              setCallStatus('speaking');
              playAudioChunk(msg.audio_event.audio_base_64);
            }
            break;
          case 'agent_response':
            setCallStatus('speaking');
            if (msg.agent_response?.text) {
              callTranscript.current.push({ role: 'assistant', content: msg.agent_response.text });
            }
            break;
          case 'user_transcript':
            setCallStatus('connected');
            if (msg.user_transcription_event?.user_transcript) {
              callTranscript.current.push({ role: 'user', content: msg.user_transcription_event.user_transcript });
            }
            break;
          case 'interruption':
            
            
            break;
          case 'ping':
            if (msg.ping_event?.event_id) ws.send(JSON.stringify({ type: 'pong', event_id: msg.ping_event.event_id }));
            break;
        }
      };
      ws.onerror = () => { setCallStatus('error'); };
      ws.onclose = () => { if (inCall) endCall(); };
    } catch (err: any) {
      alert('通话失败: ' + (err?.message || ''));
      endCall();
    }
  };

  const startMicCapture = async () => {
    if (!micStreamRef.current) return;
    try {
      micCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 } as any);
    } catch {
      micCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    await micCtxRef.current.resume();
    const rate = micCtxRef.current.sampleRate;
    const source = micCtxRef.current.createMediaStreamSource(micStreamRef.current);
    const proc = micCtxRef.current.createScriptProcessor(2048, 1, 1);
    proc.onaudioprocess = (e) => {
      if (callMuted || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      let toSend: Float32Array = input;
      if (rate !== 16000) {
        const ratio = rate / 16000;
        const newLen = Math.floor(input.length / ratio);
        toSend = new Float32Array(newLen);
        for (let i = 0; i < newLen; i++) toSend[i] = input[Math.floor(i * ratio)];
      }
      const i16 = float32ToInt16(toSend);
      wsRef.current.send(JSON.stringify({ user_audio_chunk: bufToBase64(i16.buffer) }));
    };
    source.connect(proc);
    proc.connect(micCtxRef.current.destination);
    micProcRef.current = proc;
  };

  const endCall = () => {
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    if (micProcRef.current) { try { micProcRef.current.disconnect(); } catch {} micProcRef.current = null; }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null; }
    if (micCtxRef.current) { try { micCtxRef.current.close(); } catch {} micCtxRef.current = null; }
    if (playCtxRef.current) { try { playCtxRef.current.close(); } catch {} playCtxRef.current = null; }
    if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null; }
    nextPlayTimeRef.current = 0;

    // Send transcript + duration to backend (AI memory only, call record in chat)
    const dur = callSecondsRef.current || 0;
    const transcript = [...callTranscript.current];
    callTranscript.current = [];
    // Add call record locally
    const mins = Math.floor(dur / 60);
    const secs = dur % 60;
    const durationStr = mins > 0 ? `${mins}:${String(secs).padStart(2,'0')}` : `0:${String(secs).padStart(2,'0')}`;
    if (dur > 0) {
      setMessages(p => [...p, { role: 'assistant', content: `语音通话 ${durationStr}`, timestamp: Date.now(), metadata: { type: 'voice_call', duration: dur } }]);
    }
    if (transcript.length > 0 || dur > 0) {
      fetch('/api/chat/voice/transcript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ messages: transcript, duration: dur }),
      }).catch(e => console.error('[voice] transcript save error:', e));
    }

    setInCall(false);
    setCallStatus('');
    setCallMuted(false);
  };

  const startRecording = async () => {
    if (recording || mediaRecRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      recChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
        mediaRecRef.current = null;
        const blob = new Blob(recChunksRef.current, { type: mr.mimeType });
        if (blob.size < 1000) { setRecording(false); setRecordSecs(0); return; } // too short
        const dur = callSecondsRef.current || recordSecs || 1;
        setRecording(false);
        setRecordSecs(0);
        // Upload voice message
        const fd = new FormData();
        const ext = mr.mimeType.includes('webm') ? 'webm' : 'm4a';
        fd.append('file', blob, `voice.${ext}`);
        fd.append('duration', String(dur));
        try {
          // Show user voice bubble immediately with local blob URL
          const localUrl = URL.createObjectURL(blob);
          setMessages(p => [...p, { role: 'user', content: '🎤 语音消息', timestamp: Date.now(), metadata: { type: 'voice', path: localUrl, duration: dur } }]);
          setTyping(true);
          const res = await fetch('/api/chat/voice-message', { method: 'POST', credentials: 'include', body: fd });
          if (res.ok) {
          } else {
            setTyping(false);
            setColdStartHint('');
          }
        } catch (e) { console.error('Voice upload error:', e); }
      };
      mr.start(250);
      setRecording(true);
      setRecordSecs(0);
      callSecondsRef.current = 0;
      recTimerRef.current = setInterval(() => { callSecondsRef.current++; setRecordSecs(s => s + 1); }, 1000);
      mediaRecRef.current = mr;
    } catch (e) { console.error('Mic access error:', e); }
  };
  const stopRecording = () => { if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') mediaRecRef.current.stop(); };
  const cancelRecording = () => {
    if (mediaRecRef.current) {
      try {
        if (mediaRecRef.current.state !== 'inactive') {
          mediaRecRef.current.ondataavailable = null;
          mediaRecRef.current.onstop = () => {};
          mediaRecRef.current.stop();
        }
        if (mediaRecRef.current.stream) mediaRecRef.current.stream.getTracks().forEach(t => t.stop());
      } catch {}
      mediaRecRef.current = null;
    }
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    setRecording(false);
    setRecordSecs(0);
  };

  useEffect(() => { scroll(); }, [messages, stream, typing, scroll]);

  // Mood-based music switching: watch latest assistant message
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || !last.content) return;
    const mood = detectMood(last.content);
    if (mood) {
      window.dispatchEvent(new CustomEvent('ai-mood-change', { detail: { mood } }));
    }
  }, [messages]);

  // Scroll to bottom when keyboard opens (resize event)
  useEffect(() => {
    const onResize = () => setTimeout(scroll, 100);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => window.visualViewport?.removeEventListener('resize', onResize);
  }, [scroll]);

  // Refresh messages when user comes back to foreground
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      // Force iOS to unfreeze UI after returning to foreground
      window.scrollTo(0, 0);
      // Reconnect SSE if dead
      const existingSSE = (window as any).__sseRef;
      if (existingSSE && existingSSE.readyState === 2) {
        existingSSE.close();
        // Will auto-reconnect via onerror handler
      }
      try {
        const r = await fetch('/api/chat/history', { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          if (d.messages?.length) setMessages(splitMessages(d.messages));
        }
      } catch {}
    };
    document.addEventListener('visibilitychange', onVisible);
    // Listen for SW push messages to refresh chat
    const onSWMessage = (e: MessageEvent) => {
      if (e.data?.type === 'refresh-messages') {
        fetch('/api/chat/history', { credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d?.messages?.length) { setMessages(d.messages); setTyping(false); setColdStartHint(''); } })
          .catch(() => {});
      }
    };
    navigator.serviceWorker?.addEventListener('message', onSWMessage);
    // Safety: periodically refresh messages to catch SSE misses
    const refreshWatchdog = setInterval(() => {
      fetch('/api/chat/history', { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.messages?.length) setMessages(prev => { if (d.messages.length > prev.length) { setTyping(false); setColdStartHint(''); return splitMessages(d.messages); } return prev; }); })
        .catch(() => {});
    }, 10000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onSWMessage);
      clearInterval(refreshWatchdog);
    };
  }, []);

  // PWA install detection
  useEffect(() => {
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    const dismissedAt = localStorage.getItem('pwa-hint-dismissed');
    const expired = dismissedAt ? (Date.now() - parseInt(dismissedAt)) > 7 * 24 * 60 * 60 * 1000 : true;
    if (!isInstalled && expired) { setTimeout(() => {
      setShowInstallHint(true); }, 30000);
    }
    // Show notification permission prompt if not granted yet
    if ('Notification' in window && Notification.permission !== 'granted') {
      const notifDismissed = localStorage.getItem('notif-hint-dismissed');
      if (!notifDismissed) {
        // Delay notif hint so it doesn't block initial interaction
        setTimeout(() => setShowNotifHint(true), 5000);
        // Auto-dismiss after 15s
        setTimeout(() => setShowNotifHint(false), 20000);
      }
    } else if (!('Notification' in window)) {
      // iOS Safari (non-PWA) doesn't have Notification API - show install hint instead
      console.log('Notification API not available');
    }
    const handler = (e: Event) => { e.preventDefault(); deferredPromptRef.current = e; };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    // Clean up any stale recording state on mount
    setRecording(false);
    setRecordSecs(0);
    if (mediaRecRef.current) { try { mediaRecRef.current.stop(); } catch {} mediaRecRef.current = null; }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null; }

    (async () => {
      try {
        // Check if user has a container ready
        const meRes = await fetch('/api/auth/me', { credentials: 'include' });
        if (meRes.ok) {
          const meData = await meRes.json();
          // Load AI profile
          if (meData.user?.id?.startsWith('a4fbf1d7')) { setEnhancedUI(true); }
          if (meData.user?.aiProfile?.name) { setAiName(meData.user.aiProfile.name); try { localStorage.setItem('ai_name', meData.user.aiProfile.name); } catch {} }
          if (meData.user?.aiProfile?.avatar) { const av = `/api/chat/avatar/${meData.user.id}`; setAiAvatar(av); try { localStorage.setItem('ai_avatar', av); } catch {} }
          // Dynamic manifest for personalized PWA name + icon
          if (meData.user?.id) {
            try { localStorage.setItem('pwa_user_id', meData.user.id); } catch {}
            const ml = document.getElementById('pwa-manifest');
            if (ml) (ml as HTMLLinkElement).href = `/api/chat/manifest.json?u=${meData.user.id}&t=${Date.now()}`;
          }
          if (!meData.user?.containerPort) {
            // No container yet — show provisioning UI and poll
            setProvisioning(true);
            setLoading(false);
            for (let i = 0; i < 60; i++) {
              await new Promise(r => setTimeout(r, 3000));
              const check = await fetch('/api/auth/me', { credentials: 'include' });
              if (check.ok) {
                const checkData = await check.json();
                if (checkData.user?.containerPort) { setProvisioning(false); break; }
              }
            }
          }
        }
        const r = await fetch('/api/chat/history', { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setMessages(splitMessages(d.messages || []));
          setHasMore((d.messages || []).length >= 50);
          // New user with no messages — wait for AI first message
          if ((d.messages || []).length === 0) setWaitingFirstMsg(true);
          // Ensure scroll to bottom after initial load
          setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 200);
          // Connect persistent SSE with auto-reconnect
          if (typeof EventSource !== 'undefined') {
            const connectSSE = () => {
              const es = new EventSource('/api/chat/stream', { withCredentials: true });
              (window as any).__sseRef = es;
              es.onopen = () => { setContainerStatus('online'); };
              es.onmessage = (e) => {
                try {
                  const data = JSON.parse(e.data);
                  if (data.firstMessage) {
                    setWaitingFirstMsg(false);
                    setMessages(p => [...p, { role: 'assistant', content: data.firstMessage, timestamp: Date.now() }]);
                  }
                  if (data.inject) {
                    setMessages(p => [...p, { role: data.role || 'assistant', content: data.inject, timestamp: Date.now(), ...(data.metadata ? { metadata: data.metadata } : {}) }]);
                    setTyping(false);
                    setColdStartHint('');
                    navigator.serviceWorker?.ready.then(reg => reg.getNotifications()).then(ns => ns.forEach(n => n.close())).catch(() => {});
                  }
                  // Profile updates (name/avatar changes)
                  if (typeof data.credits === 'number') { setCredits(data.credits); }
                  if (data.name && !data.inject) { setAiName(data.name); try { localStorage.setItem('ai_name', data.name); } catch {} }
                  if (typeof data.credits === 'number') { setCredits(data.credits); }
                  if (data.avatar && !data.inject) { setAiAvatar(data.avatar); try { localStorage.setItem('ai_avatar', data.avatar); } catch {} }
                  // Incoming call from AI boyfriend
                  if (data.incoming_call) {
                    setIncomingCall(true);
                    setIncomingCallOpener(data.opener || '');
                    // Play ringtone
                    try {
                      const ring = new Audio('https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3');
                      ring.loop = true;
                      ring.volume = 0.6;
                      ring.play().catch(() => {});
                      incomingCallAudioRef.current = ring;
                    } catch {}
                    // Auto-dismiss after 30 seconds
                    setTimeout(() => {
                      setIncomingCall(false);
                      if (incomingCallAudioRef.current) { incomingCallAudioRef.current.pause(); incomingCallAudioRef.current = null; }
                    }, 30000);
                  }
                } catch {}
              };
              es.onerror = () => {
                setContainerStatus('offline');
                es.close();
                // Reconnect with backoff, check container health first
                setTimeout(async () => {
                  try {
                    const r2 = await fetch('/api/chat/history', { credentials: 'include' });
                    if (r2.ok) { const d2 = await r2.json(); if (d2.messages?.length) setMessages(d2.messages); }
                  } catch {}
                  // Check if container is actually alive before reconnecting SSE
                  try {
                    const hs = await fetch('/api/container/status', { credentials: 'include' });
                    if (hs.ok) {
                      const hsd = await hs.json();
                      if (hsd.exists && hsd.ready) {
                        setContainerStatus('online');
                        connectSSE();
                      } else {
                        setContainerStatus('offline');
                        // Retry in 10s instead of spamming
                        setTimeout(connectSSE, 10000);
                      }
                    } else {
                      setContainerStatus('offline');
                      setTimeout(connectSSE, 10000);
                    }
                  } catch {
                    setContainerStatus('offline');
                    setTimeout(connectSSE, 10000);
                  }
                }, 3000);
              };
            };
            connectSSE();
          }
        }
      } catch {}
      setLoading(false);
      // Fetch container status + start periodic check
      const checkContainerStatus = async () => {
        try {
          const cs = await fetch('/api/container/status', { credentials: 'include' });
          if (cs.ok) {
            const csd = await cs.json();
            setContainerStatus(csd.exists && csd.ready ? 'online' : 'offline');
          } else { setContainerStatus('offline'); }
        } catch { setContainerStatus('offline'); }
      };
      await checkContainerStatus();
      const statusInterval = setInterval(checkContainerStatus, 30000);
      (window as any).__statusInterval = statusInterval;
      // Fetch initial credits
      try {
        const cr = await fetch('/api/chat/credits', { credentials: 'include' });
        if (cr.ok) { const cd = await cr.json(); if (typeof cd.credits === 'number') setCredits(cd.credits); }
      } catch {}
      // Clean up status poll on unmount
      window.addEventListener('beforeunload', () => clearInterval((window as any).__statusInterval));
      // iOS PWA: ensure textarea is interactive after load
      setTimeout(() => taRef.current?.blur(), 100);
    })();
    // Register push notifications
    (async () => {
      try {
        console.log('[PUSH] Starting push registration...');
        console.log('[PUSH] serviceWorker:', 'serviceWorker' in navigator, 'PushManager:', 'PushManager' in window);
        console.log('[PUSH] Notification.permission:', typeof Notification !== 'undefined' ? Notification.permission : 'N/A');
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) { console.log('[PUSH] Not supported'); return; }
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') { console.log('[PUSH] Permission denied'); return; }
        const reg = await navigator.serviceWorker.ready;
        console.log('[PUSH] SW ready');
        let sub = await reg.pushManager.getSubscription();
        console.log('[PUSH] Existing sub:', !!sub);
        if (!sub) {
          if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') { console.log('[PUSH] Permission not granted, skipping subscribe'); return; }
          const res = await fetch('/api/push/vapid-key');
          const { publicKey } = await res.json();
          if (!publicKey) { console.log('[PUSH] No VAPID key'); return; }
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });
          console.log('[PUSH] New sub created');
        }
        // Always sync subscription to server (handles previous 401 failures)
        const subJson = sub.toJSON();
        console.log('[PUSH] Syncing to server...');
        const syncRes = await fetch('/api/push/subscribe', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }),
        });
        console.log('[PUSH] Server response:', syncRes.status);
      } catch (e) { console.log('Push registration skipped:', e); }
    })();
  }, []);

  const send = async () => {
    const t = input.trim();
    if (!t) return;
    setInput('');
    setMessages(p => [...p, { role: 'user', content: t, timestamp: Date.now() }]);
    if (taRef.current) { taRef.current.style.height = '24px'; taRef.current.focus(); }

    // Don't block input — fire and forget, allow sending while AI responds
    setSending(true); setTyping(true); setStream('');

    try {
      const r = await fetch('/api/chat/send', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: t }),
      });
      if (!r.ok) throw new Error('fail');
      const reader = r.body!.getReader();
      const dec = new TextDecoder();
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of dec.decode(value, { stream: true }).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.coldStart) { setColdStartHint(d.hint || '唤醒中...'); setTyping(false); setContainerStatus('loading'); continue; }
            if (d.status) { setTyping(true); continue; } // just show typing dots
            if (d.content) { full += d.content; setStream(full); setTyping(false); setColdStartHint(''); setContainerStatus('online'); }
            else if (d.image) { setColdStartHint(''); setMessages(p => [...p, { role: 'assistant', content: '[AI发送了图片]', timestamp: Date.now(), metadata: { type: 'image', path: d.image } }]); }
            else if (d.file) { setColdStartHint(''); setMessages(p => [...p, { role: 'assistant', content: `📎 ${d.file.name}`, timestamp: Date.now(), metadata: { type: 'file', path: d.file.url, name: d.file.name, size: d.file.size } }]); }
            else if (d.done) { setColdStartHint(''); setStream(''); }
            else if (d.action === 'enable_notifications') { try { Notification.requestPermission(); } catch {} }
            else if (d.action === 'add_to_homescreen') { if (deferredPromptRef.current) { (deferredPromptRef.current as any).prompt(); } else { setShowPwaGuide(true); } }
            else if (d.type === 'voice_call') { setMessages(p => [...p, { role: 'assistant', content: `语音通话 ${d.durationStr}`, timestamp: Date.now(), metadata: { type: 'voice_call', duration: d.duration } }]); }
            else if (d.type === 'profile_update') { if (d.name) { setAiName(d.name); try { localStorage.setItem('ai_name', d.name); } catch {} } if (d.avatar) { setAiAvatar(d.avatar); try { localStorage.setItem('ai_avatar', d.avatar); } catch {} } }
            else if (d.error) throw new Error(d.error);
          } catch {}
        }
      }
      if (full) {
        // Split into separate bubbles — only short conversational text
        const parts = full.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
        const isStructured = parts.some(p => /^[\d•\-\*]|\n[\d•\-\*]/.test(p) || p.length > 40);
        const shouldSplit = parts.length > 1 && parts.length <= 3 && !isStructured;
        setMessages(p => {
          const now = Date.now();
          const newMsgs = shouldSplit
            ? parts.map((s, i) => ({ role: 'assistant' as const, content: s, timestamp: now + i }))
            : [{ role: 'assistant' as const, content: full.trim(), timestamp: now }];
          return [...p, ...newMsgs];
        });
        setStream('');
      }
    } catch { setMessages(p => [...p, { role:'assistant', content:'出了点小问题，再试一次吧 😅', error:true, timestamp:Date.now() }]); }
    finally {
      setSending(false); setTyping(false);
      // Refresh credits after message sent
      try {
        const cr = await fetch('/api/chat/credits', { credentials: 'include' });
        if (cr.ok) { const cd = await cr.json(); if (typeof cd.credits === 'number') setCredits(cd.credits); }
      } catch {}
    }
  };

  const uploadFile = async (file: File) => {
    const localUrl = URL.createObjectURL(file);
    const isImg = file.type.startsWith('image/');
    setMessages(p => [...p, {
      role: 'user', content: isImg ? '' : `[文件: ${file.name}]`,
      metadata: isImg ? { type: 'image', path: localUrl } : { type: 'file', filename: file.name },
      timestamp: Date.now(),
    }]);
    scroll();
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch('/api/chat/upload', { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) throw new Error('upload fail');
      const data = await r.json();
      // Update local URL to server URL
      setMessages(p => p.map(m => m.metadata?.path === localUrl ? { ...m, metadata: { ...m.metadata, path: data.file.path } } : m));
      // Auto-trigger AI response for images
      if (isImg) {
        setTyping(true); setStream('');
        try {
          const r2 = await fetch('/api/chat/send', {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: '[用户发送了一张图片]' }),
          });
          if (!r2.ok) throw new Error('fail');
          const reader = r2.body!.getReader();
          const dec = new TextDecoder();
          let full = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            for (const line of dec.decode(value, { stream: true }).split('\n')) {
              if (!line.startsWith('data: ')) continue;
              try {
                const d = JSON.parse(line.slice(6));
                if (d.coldStart) { setColdStartHint(d.hint || '唤醒中...'); setContainerStatus('loading'); continue; }
                if (d.status) { setColdStartHint(d.status); continue; }
                if (d.content) { full += d.content; setStream(full); setTyping(false); setColdStartHint(''); setContainerStatus('online'); }
                else if (d.image) { setColdStartHint(''); setContainerStatus('online'); setMessages(p => [...p, { role: 'assistant', content: '[AI发送了图片]', timestamp: Date.now(), metadata: { type: 'image', path: d.image } }]); }
                else if (d.file) { setColdStartHint(''); setMessages(p => [...p, { role: 'assistant', content: `📎 ${d.file.name}`, timestamp: Date.now(), metadata: { type: 'file', path: d.file.url, name: d.file.name, size: d.file.size } }]); }
                else if (d.done) { setColdStartHint(''); setStream(''); }
              } catch {}
            }
          }
          if (full) {
            setMessages(p => { const l = p[p.length-1]; return (l?.role==='assistant'&&l.content===full) ? p : [...p, {role:'assistant',content:full,timestamp:Date.now()}]; });
            setStream('');
          }
        } catch {} finally { setTyping(false); setColdStartHint(''); }
      }
    } catch {
      setMessages(p => [...p, { role: 'assistant', content: '文件发送失败 😢', error: true, timestamp: Date.now() }]);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const onKey = (e: React.KeyboardEvent) => { if (e.key==='Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) { e.preventDefault(); send(); } };
  const resize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const t = e.target; t.style.height = '24px'; t.style.height = Math.min(t.scrollHeight, 120) + 'px';
  };

  useEffect(() => {
    if (provisioning || waitingFirstMsg) {
      setShowRetry(false);
      const t = setTimeout(() => setShowRetry(true), 60000);
      return () => clearTimeout(t);
    }
  }, [provisioning, waitingFirstMsg]);

  if (loading) return (
    <div style={S.loading}><div style={S.spinner}/><span style={{color:'#a1a1aa',fontSize:'0.9rem'}}>加载中...</span></div>
  );

  if (provisioning || waitingFirstMsg) return (
    <div style={S.loading}>
      <style>{`
        @keyframes c-pulse { 0%,100%{transform:scale(1);opacity:0.7} 50%{transform:scale(1.15);opacity:1} }
        @keyframes c-spin { to{transform:rotate(360deg)} }
        @keyframes c-fade { 0%,100%{opacity:0.4} 50%{opacity:1} }
        @keyframes c-progress { 0%{width:0} 100%{width:100%} }
      `}</style>
      <div style={{width:36,height:36,border:'3px solid rgba(167,139,250,0.2)',borderTopColor:'#a78bfa',borderRadius:'50%',animation:'c-spin 0.8s linear infinite',marginBottom:20}}/>
      <span style={{color:'#e4e4e7',fontSize:'1.1rem',fontWeight:600}}>{provisioning ? '正在为你创建专属 AI 男友...' : '他正在赶来...'}</span>
      <span style={{color:'#a1a1aa',fontSize:'0.85rem',marginTop:6,animation:'c-fade 2s ease-in-out infinite'}}>{provisioning ? '初始化性格模块中' : '稍等一下，马上就好 ✨'}</span>
      {showRetry && <button onClick={() => window.location.reload()} style={{marginTop:32,padding:'10px 24px',borderRadius:20,background:'rgba(167,139,250,0.15)',color:'#a78bfa',border:'1px solid rgba(167,139,250,0.2)',cursor:'pointer',fontSize:13}}>等太久了？点击重试</button>}
    </div>
  );

  const els: React.ReactElement[] = [];
  messages.forEach((msg, i) => {
    const prev = i > 0 ? messages[i-1] : null;
    const next = i < messages.length-1 ? messages[i+1] : null;
    if (!prev || diffDay(prev, msg))
      els.push(<div key={`d${i}`} style={S.dateSep}><span style={S.dateTag}>{fmtDate(msg.created_at||msg.timestamp)}</span></div>);

    const first = !prev || prev.role !== msg.role || !sameGroup(prev, msg) || diffDay(prev, msg);
    const last = !next || next.role !== msg.role || !sameGroup(msg, next);
    const u = msg.role === 'user';
    const time = fmtTime(msg.created_at || msg.timestamp);

    els.push(
      <div key={i} style={{...S.row, justifyContent: u?'flex-end':'flex-start', marginTop: first?14:2, ...((msg.timestamp && msg.timestamp > loadedAt) ? {animation: u ? 'c-send-pop 0.25s ease-out' : 'c-msg-in 0.15s ease-out'} : {})}}>
        {!u && <div style={S.avatarCol}>{first ? <div style={S.avatar}>{aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',borderRadius:8,objectFit:'cover'}}/> : <Heart s={15}/>}</div> : <div style={{width:32}}/>}</div>}
        <div style={{maxWidth:'78%'}}>
          {!u && first && <div style={S.senderName}>{aiName}</div>}
          <div className={u ? 'enhanced-bubble-user enhanced-msg-enter' : 'enhanced-bubble-bot enhanced-msg-enter'} style={{
            ...S.bubble,
            borderRadius: u
              ? (first&&last?'18px 18px 4px 18px' : first?'18px 18px 4px 18px' : last?'18px 4px 4px 18px' : '18px 4px 4px 18px')
              : (first&&last?'18px 18px 18px 4px' : first?'18px 18px 18px 4px' : last?'4px 18px 18px 18px' : '4px 18px 18px 4px'),
          }}>
            {msg.metadata?.type === 'image' ? (
              <img src={msg.metadata.path} alt="" onClick={() => setLightbox(msg.metadata!.path)} style={{maxWidth:'100%',borderRadius:12,display:'block',cursor:'pointer'}}/>
            ) : msg.metadata?.type === 'file' ? (
              <div onClick={() => setFilePreview({ url: msg.metadata!.path, name: msg.metadata!.name || 'file' })} style={{display:'flex',alignItems:'center',gap:8,padding:'12px 16px',background:'rgba(255,255,255,0.1)',borderRadius:12,cursor:'pointer',color:'#fff'}}>
                <span style={{fontSize:28}}>📎</span>
                <div>
                  <div style={{fontWeight:600}}>{msg.metadata.name}</div>
                  {msg.metadata.size && <div style={{fontSize:12,opacity:0.6}}>{(msg.metadata.size/1024).toFixed(1)} KB</div>}
                </div>
              </div>
            ) : msg.metadata?.type === 'voice_call' ? (
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background:'rgba(167,139,250,0.08)',borderRadius:16,minWidth:140}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.6}}>
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                <span style={{fontSize:13,opacity:0.7}}>{msg.content}</span>
              </div>
            ) : msg.metadata?.type === 'voice' ? (
              <button onClick={() => playTTS('', i, msg.metadata!.path)} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background: ttsPlaying === i ? 'rgba(167,139,250,0.25)' : 'rgba(167,139,250,0.12)',borderRadius:18,border:'none',color:'#fff',cursor:'pointer',minWidth:150}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0,opacity: ttsPlaying === i ? 1 : 0.8}}>
                  {ttsPlaying === i
                    ? <><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></>
                    : <path d="M8 5v14l11-7z"/>}
                </svg>
                <div style={{flex:1,display:'flex',alignItems:'center',gap:1.5,height:24}}>
                  {[...Array(20)].map((_,j) => <div key={j} style={{width:2,height:4+Math.sin(j*0.7)*8+Math.random()*4,background: ttsPlaying === i ? 'rgba(167,139,250,0.9)' : 'rgba(167,139,250,0.4)',borderRadius:1,transition:'height 0.15s'}}/>)}
                </div>
                <span style={{fontSize:12,opacity:0.5,whiteSpace:'nowrap',fontVariantNumeric:'tabular-nums'}}>{ttsDurations[i] ? `${ttsDurations[i]}″` : ''}</span>
              </button>
            ) : u ? <span style={{whiteSpace:'pre-wrap'}}>{msg.content}</span>
               : <div className="c-md" dangerouslySetInnerHTML={{__html: md(msg.content || '')}}/>}
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:2}}>
              <span style={{...S.time, color: u?'rgba(255,255,255,0.5)':'rgba(255,255,255,0.3)'}}>{time}</span>
            </div>
          </div>
          {!u && i === messages.length - 1 && (() => {
            const { options } = parseOptions(msg.content || '');
            return options.length > 0 ? (
              <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:8}}>
                {options.map((opt, j) => (
                  <button key={j} onClick={() => { setInput(opt); setTimeout(() => send(), 0); }} style={{padding:'8px 16px',borderRadius:20,background:'rgba(167,139,250,0.15)',color:'#fff',border:'1px solid rgba(167,139,250,0.3)',cursor:'pointer',fontSize:14,transition:'background 0.2s'}}>
                    {opt}
                  </button>
                ))}
              </div>
            ) : null;
          })()}
        </div>
      </div>
    );
  });

  // Time-based background for all users
  const sceneStyle: React.CSSProperties = {
    position: 'absolute' as const, inset: 0, zIndex: 0,
    background: currentHour >= 6 && currentHour < 12
      ? 'linear-gradient(180deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)'  // morning
      : currentHour >= 12 && currentHour < 18
      ? 'linear-gradient(180deg, #0f0c29 0%, #302b63 50%, #24243e 100%)'  // afternoon
      : currentHour >= 18 && currentHour < 22
      ? 'linear-gradient(180deg, #141e30 0%, #243b55 100%)'               // evening
      : 'linear-gradient(180deg, #0a0a0f 0%, #1a0a2e 50%, #0d0d1a 100%)', // night
    transition: 'background 2s ease',
  };

  // Base visual upgrade CSS — all users get frosted glass bubbles + enhanced header/input
  const baseCSS = `
    .enhanced-bubble-bot {
      background: rgba(24,24,27,0.6) !important;
      backdrop-filter: blur(16px) saturate(1.3) !important;
      -webkit-backdrop-filter: blur(16px) saturate(1.3) !important;
      border: 1px solid rgba(167,139,250,0.15) !important;
      box-shadow: 0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05) !important;
    }
    .enhanced-bubble-user {
      background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 50%, #5b21b6 100%) !important;
      box-shadow: 0 4px 20px rgba(124,58,237,0.4) !important;
      border: none !important;
    }
    .enhanced-header {
      background: rgba(10,10,15,0.7) !important;
      backdrop-filter: blur(20px) !important;
      -webkit-backdrop-filter: blur(20px) !important;
      border-bottom: 1px solid rgba(167,139,250,0.1) !important;
    }
    .enhanced-input-wrap {
      background: rgba(10,10,15,0.7) !important;
      backdrop-filter: blur(20px) !important;
      -webkit-backdrop-filter: blur(20px) !important;
      border-top: 1px solid rgba(167,139,250,0.1) !important;
    }
    .enhanced-textarea {
      background: rgba(24,24,27,0.5) !important;
      border: 1px solid rgba(167,139,250,0.2) !important;
      transition: border-color 0.3s, box-shadow 0.3s !important;
    }
    .enhanced-textarea:focus {
      border-color: rgba(167,139,250,0.5) !important;
      box-shadow: 0 0 20px rgba(167,139,250,0.15) !important;
    }
    .enhanced-msg-enter {
      animation: msg-slide-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    @keyframes msg-slide-in {
      from { opacity: 0; transform: translateY(16px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  `;

  // Premium particle effects — only for enhancedUI users
  const particleCSS = enhancedUI ? `
    @keyframes float-particle {
      0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
      50% { transform: translateY(-20px) scale(1.2); opacity: 0.6; }
    }
    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes pulse-glow {
      0%, 100% { box-shadow: 0 0 20px rgba(167,139,250,0.1); }
      50% { box-shadow: 0 0 40px rgba(167,139,250,0.25); }
    }
    .enhanced-bubble-bot { animation: pulse-glow 4s ease-in-out infinite; }
    .enhanced-avatar {
      box-shadow: 0 0 16px rgba(167,139,250,0.3);
      animation: pulse-glow 3s ease-in-out infinite;
    }
    .particle {
      position: absolute;
      width: 3px; height: 3px;
      border-radius: 50%;
      background: rgba(167,139,250,0.4);
      pointer-events: none;
    }
  ` : '';

  return (
    <div id="chat-root" style={{...S.root, position: 'relative' as const, overflow: 'hidden'}}>
      <style>{baseCSS}{particleCSS}</style>
      <div style={sceneStyle}>
        {/* Floating particles — only for premium users */}
        {enhancedUI && [...Array(12)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${8 + (i * 7.5) % 85}%`,
            top: `${10 + (i * 13) % 80}%`,
            width: 2 + (i % 3),
            height: 2 + (i % 3),
            opacity: 0.2 + (i % 4) * 0.1,
            animation: `float-particle ${3 + (i % 4)}s ease-in-out ${i * 0.5}s infinite`,
          }} />
        ))}
      </div>
      {/* Header */}
      <div style={{...S.header, position: "relative" as const, zIndex: 10}} className="enhanced-header">
        <div style={S.headerAvatar} onClick={()=>window.dispatchEvent(new CustomEvent('navigate',{detail:{page:'bf-settings'}}))}>{aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',borderRadius:8,objectFit:'cover'}} onError={(e) => (e.target as HTMLImageElement).style.display='none'}/> : <Heart s={18}/>}</div>
        <div style={{flex:1,cursor:'pointer'}} onClick={()=>window.dispatchEvent(new CustomEvent('navigate',{detail:{page:'bf-settings'}}))}>
          <div style={S.headerName}>{aiName}</div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{fontSize:12,color: containerStatus === 'online' ? '#22c55e' : containerStatus === 'loading' ? '#a1a1aa' : '#ef4444'}}>
                {containerStatus === 'online' ? '● 在线' : containerStatus === 'loading' ? '● 唤醒中...' : '○ 离线'}
              </div>
              {credits !== null && <span style={{fontSize:11,color:'#a78bfa'}}>⚡ {credits.toFixed(1)}</span>}
            </div>
        </div>
        <button style={S.logoutBtn} onClick={() => { if(confirm('确定要退出登录吗？')) fetch('/api/auth/logout', {method:'POST',credentials:'include'}).then(()=>location.href='/'); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
      </div>

      {/* Messages */}
      <div ref={listRef} style={{...S.messages, position: "relative" as const, zIndex: 1}} onScroll={(e) => { if ((e.target as HTMLDivElement).scrollTop < 50) loadMore(); }}>
        {loadingMore && <div style={{textAlign:'center',padding:10,color:'#a1a1aa',fontSize:13}}>加载中...</div>}
        {!hasMore && messages.length > 0 && <div style={{textAlign:'center',padding:10,color:'#52525b',fontSize:12}}>没有更多消息了</div>}
        {messages.length===0 && !typing && (
          <div style={S.empty}>
            <div style={S.emptyIcon}>{waitingFirstMsg ? '💫' : <Heart s={40}/>}</div>
            <p style={{fontSize:'1.05rem',fontWeight:500,color:'#fafafa',margin:'0 0 4px'}}>{waitingFirstMsg ? '他正在赶来...' : '开始聊天吧'}</p>
            <p style={{fontSize:'0.85rem',margin:0}}>{waitingFirstMsg ? '稍等一下，马上就好 ✨' : '随时和我说话，我一直在 ✨'}</p>
          </div>
        )}
        {els}
        {coldStartHint && (
          <div style={{...S.row, marginTop:14}}>
            <div style={S.avatarCol}><div style={S.avatar}>{aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',borderRadius:8,objectFit:'cover'}}/> : <Heart s={15}/>}</div></div>
            <div style={{...S.bubble,...S.bubbleBot,borderRadius:'18px 18px 18px 4px',display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:'0.85rem',color:'#a1a1aa'}}>{coldStartHint.includes('唤醒') ? '😴' : coldStartHint.includes('搜索') ? '🔍' : coldStartHint.includes('图片') ? '🎨' : coldStartHint.includes('回忆') ? '💭' : coldStartHint.includes('提醒') ? '⏰' : '💫'} {coldStartHint}</span>
            </div>
          </div>
        )}
        {typing && !coldStartHint && (
          <div style={{...S.row, marginTop:14}}>
            <div style={S.avatarCol}><div style={S.avatar}>{aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',borderRadius:8,objectFit:'cover'}}/> : <Heart s={15}/>}</div></div>
            <div style={{...S.bubble,...S.bubbleBot,borderRadius:'18px 18px 18px 4px',display:'flex',alignItems:'center',gap:8}}>
              <div style={{display:'flex',alignItems:'center',gap:2,height:20}}>
                {[...Array(5)].map((_,j) => <div key={j} style={{width:3,borderRadius:1.5,background:'rgba(167,139,250,0.6)',animation:`typing-wave 1.2s ease-in-out ${j*0.1}s infinite`}}/>)}
              </div>
              <span style={{fontSize:'0.8rem',color:'#a1a1aa'}}>正在输入</span>
            </div>
          </div>
        )}
        {stream && (
          <div style={{...S.row, marginTop:14}}>
            <div style={S.avatarCol}><div style={S.avatar}>{aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',borderRadius:8,objectFit:'cover'}}/> : <Heart s={15}/>}</div></div>
            <div style={{maxWidth:'78%'}}>
              <div style={{...S.bubble,...S.bubbleBot,borderRadius:'18px 18px 18px 4px',borderColor:'rgba(167,139,250,0.3)',boxShadow:'0 0 12px rgba(167,139,250,0.1)'}}>
                <div className="c-md" dangerouslySetInnerHTML={{__html: md(stream)}}/>
                <span style={S.cursor}/>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{...S.inputWrap, position: "relative" as const, zIndex: 10}} className="enhanced-input-wrap">
        <div style={S.inputRow}>
          <input ref={fileRef} id="bf-file-input" type="file" accept="image/*,audio/*,video/*,.pdf" style={{display:'none'}} onChange={(e) => { setShowPlusMenu(false); onFileChange(e); }}/>
          <div style={{position:'relative'}}>
            <button style={{...S.attachBtn, transform: showPlusMenu ? 'rotate(45deg)' : 'none', transition:'transform 0.2s'}} title="更多" onClick={() => setShowPlusMenu(!showPlusMenu)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
            {showPlusMenu && (
              <div style={{position:'absolute',bottom:'100%',left:0,marginBottom:8,background:'#1e1e2e',borderRadius:12,padding:6,display:'flex',flexDirection:'column',gap:2,boxShadow:'0 4px 20px rgba(0,0,0,0.4)',minWidth:140,zIndex:1000}}>
                <label htmlFor="bf-file-input" onClick={() => setTimeout(() => setShowPlusMenu(false), 200)} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'none',border:'none',color:'#fff',fontSize:14,cursor:'pointer',borderRadius:8,whiteSpace:'nowrap'}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A855F7" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  图片/文件
                </label>
                <button onClick={() => { setShowPlusMenu(false); inCall ? endCall() : startCall(); }} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'none',border:'none',color: inCall ? '#ef4444' : '#fff',fontSize:14,cursor:'pointer',borderRadius:8,whiteSpace:'nowrap'}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={inCall ? '#ef4444' : '#A855F7'}>
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  {inCall ? '挂断通话' : '语音通话'}
                </button>
              </div>
            )}
          </div>
          {recording ? (
            <div style={{flex:1,display:'flex',alignItems:'center',gap:10,padding:'0 4px',height:42}}>
              <button onClick={cancelRecording} style={{background:'none',border:'none',color:'#ef4444',fontSize:22,cursor:'pointer',padding:'2px 6px',lineHeight:1}}>✕</button>
              <div style={{width:8,height:8,borderRadius:4,background:'#ef4444',animation:'pulse 1s infinite',flexShrink:0}}/>
              <span style={{fontSize:15,color:'#fff',fontVariantNumeric:'tabular-nums',flex:1}}>{Math.floor(recordSecs/60)}:{String(recordSecs%60).padStart(2,'0')}</span>
              <button onClick={stopRecording} style={{background:'linear-gradient(135deg,#FF6B9D,#A855F7)',border:'none',borderRadius:'50%',width:36,height:36,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',flexShrink:0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
              </button>
            </div>
          ) : (
            <>
              <textarea ref={taRef} value={input} onChange={resize} onKeyDown={onKey}
                className="enhanced-textarea"
                placeholder={waitingFirstMsg ? "等他先说话..." : "消息..."} rows={1}
                style={{...S.textarea, ...(waitingFirstMsg ? {opacity:0.4} : {})}}
                disabled={waitingFirstMsg}
                onTouchEnd={(e) => { if (waitingFirstMsg) return; e.preventDefault(); (e.target as HTMLTextAreaElement).focus(); }}
                enterKeyHint="send"/>
              {input.trim() ? (
                <button onClick={send} style={{...S.sendBtn, opacity:1}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                </button>
              ) : (
                <button
                  onClick={() => startRecording()}
                  style={{...S.sendBtn, opacity:0.6}}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Click outside to close plus menu */}
      

      {/* Notification permission hint */}
      {showNotifHint && (
        <div style={{position:'fixed',top:56,left:0,right:0,background:'linear-gradient(135deg,#FF6B9D,#A855F7)',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',zIndex:9000,boxShadow:'0 2px 10px rgba(0,0,0,0.2)'}}>
          <div style={{color:'white',fontSize:13}}>
            🔔 开启通知，不错过他的消息
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={async () => {
              const perm = await Notification.requestPermission();
              if (perm === 'granted') {
                setShowNotifHint(false);
                // Now register push subscription
                try {
                  const reg = await navigator.serviceWorker.ready;
                  const res = await fetch('/api/push/vapid-key');
                  const { publicKey } = await res.json();
                  if (publicKey) {
                    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
                    const subJson = sub.toJSON();
                    await fetch('/api/push/subscribe', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }) });
                  }
                } catch (e) { console.log('Push sub failed:', e); }
              } else {
                setShowNotifHint(false);
                localStorage.setItem('notif-hint-dismissed', '1');
              }
            }} style={{background:'white',color:'#A855F7',border:'none',borderRadius:6,padding:'6px 14px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              开启
            </button>
            <button onClick={() => { setShowNotifHint(false); localStorage.setItem('notif-hint-dismissed', '1'); }} style={{background:'transparent',color:'rgba(255,255,255,0.8)',border:'1px solid rgba(255,255,255,0.4)',borderRadius:6,padding:'6px 10px',fontSize:12,cursor:'pointer'}}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* PWA guide modal */}
      {showPwaGuide && (() => {
        const ua = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad/.test(ua);
        const isHuawei = /huawei|harmonyos/.test(ua);
        const isXiaomi = /xiaomi|miui|redmi/.test(ua);
        const isVivo = /vivo/.test(ua);
        const isOppo = /oppo|realme|coloros/.test(ua);
        const isSamsung = /samsung/.test(ua) || /samsungbrowser/.test(ua);
        const isChrome = /chrome/.test(ua) && !/edge|edg/.test(ua);

        let steps: string[] = [];
        let title = '';
        if (isIOS) {
          title = 'Safari 浏览器';
          steps = ['点击底部中间的 分享按钮 ↑', '向上滑动菜单', '点击「添加到主屏幕」', '点击右上角「添加」'];
        } else if (isHuawei) {
          title = '华为浏览器';
          steps = ['点击右上角 ⋮ 三个点', '点击「添加至桌面」', '如果是经典外观：点右下角 ⋮ → 添加至 → 桌面'];
        } else if (isXiaomi) {
          title = '小米浏览器';
          steps = ['点击底部 ☰ 三道横线', '上滑找到「添加到桌面」', '注意：小米浏览器发图可能有 BUG，建议用 Chrome'];
        } else if (isVivo) {
          title = 'Vivo 浏览器';
          steps = ['点击底部 ☰ 三道横线', '点击「添加书签」', '点击「编辑」', '选中「手机桌面」', '点击「保存」'];
        } else if (isOppo) {
          title = 'OPPO 浏览器';
          steps = ['点击底部 ☰ 菜单', '选择「添加到桌面」或「创建快捷方式」'];
        } else if (isSamsung) {
          title = '三星浏览器';
          steps = ['点击右上角 ⋮ 三个点', '点击「添加至主屏幕」'];
        } else if (isChrome) {
          title = 'Chrome 浏览器';
          steps = ['点击右上角 ⋮ 三个点', '点击「添加到主屏幕」或「安装应用」'];
        } else {
          title = '添加到桌面';
          steps = ['点击浏览器菜单（通常是 ⋮ 或 ☰）', '找到「添加到主屏幕」或「添加到桌面」', '如果没有，请先在手机设置中允许浏览器「创建桌面快捷方式」权限'];
        }

        return (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={() => setShowPwaGuide(false)}>
            <div onClick={e => e.stopPropagation()} style={{background:'#1a1a2e',borderRadius:20,padding:'28px 24px',maxWidth:340,width:'100%',color:'#fff'}}>
              <div style={{textAlign:'center',marginBottom:20}}>
                <div style={{fontSize:40,marginBottom:8}}>📱</div>
                <div style={{fontSize:18,fontWeight:700}}>添加到手机桌面</div>
                <div style={{fontSize:13,opacity:0.6,marginTop:4}}>打开更快，体验更好</div>
              </div>
              <div style={{background:'rgba(167,139,250,0.1)',borderRadius:12,padding:16,marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:600,color:'#A855F7',marginBottom:10}}>{title}</div>
                {steps.map((s, i) => (
                  <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',marginBottom:i < steps.length - 1 ? 10 : 0}}>
                    <div style={{width:22,height:22,borderRadius:11,background:'linear-gradient(135deg,#FF6B9D,#A855F7)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0}}>{i+1}</div>
                    <div style={{fontSize:13,lineHeight:'22px',opacity:0.9}}>{s}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowPwaGuide(false)} style={{width:'100%',padding:'12px',background:'linear-gradient(135deg,#FF6B9D,#A855F7)',border:'none',borderRadius:12,color:'#fff',fontSize:15,fontWeight:600,cursor:'pointer'}}>
                我知道了
              </button>
            </div>
          </div>
        );
      })()}

      {/* File preview overlay */}
      {filePreview && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:10001,display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:'rgba(0,0,0,0.5)'}}>
            <span style={{color:'#fff',fontSize:14,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{filePreview.name}</span>
            <div style={{display:'flex',gap:12}}>
              <a href={filePreview.url} download={filePreview.name} style={{color:'#A855F7',fontSize:13,textDecoration:'none',padding:'6px 12px',border:'1px solid #A855F7',borderRadius:8}}>下载</a>
              <button onClick={() => setFilePreview(null)} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer',padding:'0 4px'}}>✕</button>
            </div>
          </div>
          <iframe src={filePreview.url} style={{flex:1,border:'none',background:'#fff'}}/>
        </div>
      )}

      {/* PWA install hint */}
      {showInstallHint && (
        <div style={{position:'fixed',bottom:0,left:0,right:0,background:'linear-gradient(135deg,#FF6B9D,#A855F7)',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',zIndex:9000,boxShadow:'0 -2px 10px rgba(0,0,0,0.2)'}}>
          <div style={{color:'white',fontSize:13}}>
            📱 添加到桌面，获得更好体验
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={async () => {
              if (deferredPromptRef.current) {
                deferredPromptRef.current.prompt();
                const result = await deferredPromptRef.current.userChoice;
                if (result.outcome === 'accepted') setShowInstallHint(false);
              } else {
                alert('请点击浏览器菜单中的「添加到主屏幕」或「安装应用」');
              }
            }} style={{background:'white',color:'#A855F7',border:'none',borderRadius:6,padding:'6px 14px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              安装
            </button>
            <button onClick={() => { setShowInstallHint(false); localStorage.setItem('pwa-hint-dismissed', String(Date.now())); }} style={{background:'transparent',color:'rgba(255,255,255,0.8)',border:'1px solid rgba(255,255,255,0.4)',borderRadius:6,padding:'6px 10px',fontSize:12,cursor:'pointer'}}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Voice call overlay */}
      {inCall && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'linear-gradient(135deg,#0a0a1a,#1a1a3e,#0f2040)',zIndex:9999,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'white'}}>
          <div style={{width:120,height:120,borderRadius:24,background:'linear-gradient(135deg,#667eea,#764ba2)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:20,overflow:'hidden'}}>
            {aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : <svg width="48" height="48" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>}
          </div>
          <div style={{fontSize:22,fontWeight:600,marginBottom:4}}>{aiName}</div>
          <div style={{fontSize:13,color:'rgba(255,255,255,0.5)',marginBottom:50}}>
            {callStatus === 'connecting' ? '连接中...' : callStatus === 'waiting' ? '等待中...' : callStatus === 'speaking' ? `${aiName}在说话...` : callStatus === 'error' ? '连接失败' : '通话中'}
          </div>
          <div style={{display:'flex',gap:36,alignItems:'center'}}>
            <button onClick={() => setCallMuted(m => !m)} style={{width:60,height:60,borderRadius:'50%',border:'none',background:callMuted?'rgba(255,255,255,0.35)':'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d={callMuted ? "M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V20c0 .55.45 1 1 1s1-.45 1-1v-2.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" : "M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"}/></svg>
            </button>
            <button onClick={endCall} style={{width:60,height:60,borderRadius:'50%',border:'none',background:'#ef4444',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>
            </button>
          </div>
          <div style={{fontSize:15,color:'rgba(255,255,255,0.5)',marginTop:24,fontVariantNumeric:'tabular-nums'}}>
            {String(Math.floor(callSeconds/60)).padStart(2,'0')}:{String(callSeconds%60).padStart(2,'0')}
          </div>
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position:'fixed',top:0,left:0,right:0,bottom:0,
          background:'rgba(0,0,0,0.9)',zIndex:9999,
          display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
          cursor:'pointer',
        }}>
          <img src={lightbox} alt="" style={{maxWidth:'95vw',maxHeight:'85vh',objectFit:'contain',borderRadius:8}}/>
          <button onClick={(e) => {
            e.stopPropagation();
            const a = document.createElement('a');
            a.href = lightbox;
            a.download = `photo-${Date.now()}.jpg`;
            a.click();
          }} style={{
            marginTop:16,padding:'10px 28px',
            background:'rgba(255,255,255,0.15)',backdropFilter:'blur(8px)',
            border:'1px solid rgba(255,255,255,0.2)',borderRadius:24,
            color:'#fff',fontSize:15,cursor:'pointer',
          }}>保存到本地</button>
        </div>
      )}

      <style>{`
        /* Prevent horizontal scroll globally */
        html, body { overflow-x: hidden !important; max-width: 100vw !important; height: 100% !important; } #chat-root { position: fixed !important; top: 0; left: 0; right: 0; bottom: 0; }
        /* iOS input zoom prevention */
        input, textarea, select { font-size: 16px !important; }
        @keyframes c-msg-in { from{opacity:0;transform:translateY(6px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes c-send-pop { 0%{opacity:0;transform:translateY(20px) scale(0.9)} 50%{transform:translateY(-2px) scale(1.02)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes c-bounce { 0%,80%,100%{transform:scale(0.8);opacity:0.4} 40%{transform:scale(1.1);opacity:1} }
        @keyframes c-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes typing-wave { 0%,100%{height:6px} 50%{height:18px} }
        @keyframes c-spin { to{transform:rotate(360deg)} }
        .c-md p{margin:0.35em 0}.c-md p:first-child{margin-top:0}.c-md p:last-child{margin-bottom:0}
        .c-md code{background:rgba(0,0,0,0.05);padding:2px 6px;border-radius:4px;font-family:'SF Mono','JetBrains Mono','Consolas',monospace;font-size:0.85em;color:#07C160;border:1px solid rgba(0,0,0,0.08)}
        .c-md pre{background:#f5f5f5;padding:12px;border-radius:8px;overflow-x:auto;margin:8px 0;border:1px solid #e0e0e0}
        .c-md pre code{background:none;padding:0;color:#1a1a1a;border:none;font-size:0.88em}
        .c-md a{color:#576B95;text-decoration:none}.c-md a:hover{text-decoration:underline}
        .c-md blockquote{border-left:2px solid #07C160;padding-left:12px;margin:8px 0;color:#666}
        .c-md ul,.c-md ol{padding-left:20px;margin:4px 0}.c-md strong{color:#1a1a1a}
        .c-md hr{border:none;border-top:1px solid #e0e0e0;margin:12px 0}
      `}</style>
      {/* Incoming Call Overlay */}
      {incomingCall && (
        <div style={{position:'fixed',inset:0,background:'linear-gradient(180deg,#0a0a0f 0%,#1a1a2e 50%,#0a0a0f 100%)',zIndex:9999,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:24}}>
          <style>{`
            @keyframes pulse-ring { 0% { transform: scale(0.95); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.95); opacity: 0.7; } }
            @keyframes shake { 0%,100% { transform: rotate(0deg); } 25% { transform: rotate(-15deg); } 75% { transform: rotate(15deg); } }
          `}</style>
          <div style={{position:'relative'}}>
            <div style={{width:120,height:120,borderRadius:'50%',background:'linear-gradient(135deg,#a78bfa,#7c3aed)',animation:'pulse-ring 2s ease-in-out infinite',position:'absolute',inset:-10}} />
            <div style={{width:100,height:100,borderRadius:'50%',background:'linear-gradient(135deg,#a78bfa,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:48,position:'relative',overflow:'hidden'}}>
              {aiAvatar ? <img src={aiAvatar} style={{width:'100%',height:'100%',objectFit:'cover'}} /> : '💕'}
            </div>
          </div>
          <div style={{color:'#fff',fontSize:22,fontWeight:700,marginTop:8}}>{aiName || '他'}</div>
          <div style={{color:'#a1a1aa',fontSize:14}}>来电中...</div>
          {incomingCallOpener && <div style={{color:'#a78bfa',fontSize:13,fontStyle:'italic',marginTop:4}}>{incomingCallOpener}</div>}
          <div style={{display:'flex',gap:60,marginTop:40}}>
            <div style={{textAlign:'center'}}>
              <button onClick={rejectIncomingCall} style={{width:64,height:64,borderRadius:'50%',background:'#ef4444',border:'none',color:'#fff',fontSize:28,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                ✕
              </button>
              <div style={{color:'#a1a1aa',fontSize:12,marginTop:8}}>拒接</div>
            </div>
            <div style={{textAlign:'center'}}>
              <button onClick={answerIncomingCall} style={{width:64,height:64,borderRadius:'50%',background:'#22c55e',border:'none',color:'#fff',fontSize:28,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',animation:'shake 0.5s ease-in-out infinite'}}>
                📞
              </button>
              <div style={{color:'#a1a1aa',fontSize:12,marginTop:8}}>接听</div>
            </div>
          </div>
        </div>
      )}
      <MusicPlayer />
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: { height:'100vh', height:'100dvh', display:'flex', flexDirection:'column', background:'#EDEDED', color:'#1a1a1a', fontFamily:"-apple-system,'SF Pro Display','Inter','PingFang SC','Noto Sans SC',sans-serif", WebkitFontSmoothing:'antialiased', fontSize:15, overflow:'hidden' },
  // Header
  header: { display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#EDEDED', borderBottom:'1px solid #D9D9D9', flexShrink:0 },
  backBtn: { background:'none', border:'none', color:'#1a1a1a', padding:4, cursor:'pointer', display:'flex', borderRadius:6 },
  logoutBtn: { background:'none', border:'none', color:'#1a1a1a', padding:6, cursor:'pointer', display:'flex', borderRadius:6 },
  headerAvatar: { width:36, height:36, borderRadius:4, background:'linear-gradient(135deg,#a78bfa,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', overflow:'hidden' },
  headerName: { fontWeight:600, fontSize:'0.95rem', letterSpacing:'-0.01em', color:'#1a1a1a' },
  headerStatus: { fontSize:'0.75rem', color:'#07C160' },
  // Messages
  messages: { flex:1, overflowY:'auto' as const, overflowX:'hidden' as const, padding:'12px 16px 20px', overscrollBehavior:'contain', WebkitOverflowScrolling:'touch' as any, background:'#EDEDED' },
  dateSep: { textAlign:'center' as const, margin:'20px 0 8px' },
  dateTag: { fontSize:'0.7rem', color:'#999', background:'#CECECE', padding:'4px 12px', borderRadius:4 },
  row: { display:'flex', alignItems:'flex-end' },
  avatarCol: { width:32, marginRight:8, flexShrink:0 },
  avatar: { width:36, height:36, borderRadius:4, background:'linear-gradient(135deg,#a78bfa,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', overflow:'hidden' },
  senderName: { fontSize:'0.7rem', color:'#999', marginBottom:3, marginLeft:2, fontWeight:500 },
  bubble: { position:'relative' as const, padding:'10px 14px', lineHeight:1.6, wordBreak:'break-word' as const, overflowWrap:'anywhere' as const, fontSize:'0.935rem', maxWidth:'80%', borderRadius:4 },
  bubbleUser: { background:'#95EC69', color:'#1a1a1a' },
  bubbleBot: { background:'#fff', color:'#1a1a1a', border:'none', boxShadow:'0 1px 1px rgba(0,0,0,0.05)' },
  time: { fontSize:'0.65rem', lineHeight:1, fontVariantNumeric:'tabular-nums', flexShrink:0, alignSelf:'flex-end' as const, paddingBottom:1, color:'#999' },
  // Typing
  dots: { display:'flex', gap:5, padding:'4px 2px', alignItems:'center' },
  dot: { width:7, height:7, borderRadius:'50%', background:'#999', animation:'c-bounce 1.2s infinite', display:'inline-block' },
  cursor: { display:'inline-block', width:2, height:'1em', background:'#07C160', animation:'c-blink 0.8s infinite', verticalAlign:'text-bottom', marginLeft:2 },
  // Empty
  empty: { textAlign:'center' as const, padding:'80px 20px 40px', color:'#999' },
  emptyIcon: { display:'inline-flex', padding:16, background:'#fff', borderRadius:'50%', border:'1px solid #e0e0e0', marginBottom:16, color:'#07C160' },
  // Input
  inputWrap: { flexShrink:0, padding:'6px 8px', paddingBottom:'max(6px, env(safe-area-inset-bottom))', background:'#F7F7F7', borderTop:'1px solid #E0E0E0', width:'100%', boxSizing:'border-box' as const },
  inputRow: { display:'flex', alignItems:'flex-end', gap:6, width:'100%', boxSizing:'border-box' as const },
  textarea: { flex:1, background:'#fff', border:'1px solid #E0E0E0', borderRadius:4, color:'#1a1a1a', fontSize:16, resize:'none' as const, outline:'none', minHeight:40, maxHeight:120, fontFamily:'inherit', lineHeight:1.4, padding:'9px 14px', boxSizing:'border-box' as const },
  attachBtn: { width:40, height:40, minWidth:40, minHeight:40, maxHeight:40, borderRadius:4, background:'none', border:'none', color:'#666', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, alignSelf:'flex-end' as const },
  sendBtn: { width:40, height:40, minWidth:40, minHeight:40, maxHeight:40, borderRadius:4, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', background:'#07C160', color:'#fff', transition:'opacity 0.15s', flexShrink:0, alignSelf:'flex-end' as const },

  // Loading
  loading: { height:'100vh', display:'flex', flexDirection:'column' as const, alignItems:'center', justifyContent:'center', background:'#EDEDED', gap:12 },
  spinner: { width:32, height:32, border:'2px solid #D9D9D9', borderTopColor:'#07C160', borderRadius:'50%', animation:'c-spin 0.7s linear infinite' },
};
